import isEqual from 'lodash/isEqual';
import { convertPxToVw } from 'utils/helpers';
import type { DesignDefinition, DesignModeSelection, DesignModeValue, DesignToken, DesignTokenSet } from './types';
import { assetIdToReference, dimensionTokenIdToCssVariable, tokenIdToCssVariable } from './operations';
import { getDefaultDesignModeSelection, resolveDesignModeValue } from './modes';
import { isDesignTokenTypeCompatible, DESIGN_TOKEN_PROPERTY_CONFIG } from './properties';

export type {
	DesignAsset,
	DesignAssetRole,
	DesignAssetSet,
	DesignDefinition,
	DesignMode,
	DesignModeActivation,
	DesignModeGroup,
	DesignModeSelection,
	DesignModeValue,
	DesignToken,
	DesignTokenSet,
	StoryDesignModeSettings,
	TokenCollection,
	DesignTokenType,
} from './types';
export * from './operations';
export * from './values';
export * from './properties';
export * from './modes';

const TOKEN_VARIABLE_REGEXP = /var\(\s*(--sc-token-[a-z0-9-]+)(?:\s*,[^)]*)?\)/gi;
const EXACT_TOKEN_VARIABLE_REGEXP = /^var\(\s*(--sc-token-[a-z0-9-]+)(?:\s*,[^)]*)?\)$/i;
export type CompiledDesignToken = DesignToken & {
	collectionId: string;
	collectionName: string;
	cssVariable: string;
	reference: string;
};
export function flattenDesignTokens(tokens?: DesignTokenSet): CompiledDesignToken[] {
	if (!tokens) return [];
	return tokens.collections.flatMap(collection =>
		collection.tokens.map(token => ({
			...token,
			collectionId: collection.id,
			collectionName: collection.name,
			cssVariable: tokenIdToCssVariable(token.id),
			reference: `var(${tokenIdToCssVariable(token.id)})`,
		}))
	);
}
export function compileForAI(tokens?: DesignTokenSet) {
	return flattenDesignTokens(tokens).map(({ collectionName, id, name, type, value, reference, description }) => ({
		collectionName,
		id,
		name,
		type,
		value,
		reference,
		...(description ? { description } : {}),
	}));
}
function createTokenDeclarations(
	tokens: CompiledDesignToken[],
	selection: DesignModeSelection,
	dimensionPlatforms: Record<string, number>
): string {
	return tokens
		.flatMap(token => {
			const value = resolveDesignModeValue(token.value as DesignModeValue<string | number>, selection);
			const declarations = [`\t${token.cssVariable}: ${value};`];
			if (token.type === 'dimension' && /^[-+]?(?:\d+(?:\.\d+)?|\.\d+)px$/i.test(String(value))) {
				for (const [platform, referenceWidth] of Object.entries(dimensionPlatforms))
					declarations.push(
						`\t${dimensionTokenIdToCssVariable(token.id, platform)}: ${convertPxToVw(value, 2, referenceWidth)};`
					);
			}
			return declarations;
		})
		.join('\n');
}
export function createDesignTokenCss(
	design?: DesignDefinition,
	selector = ':root',
	dimensionPlatforms: Record<string, number> = {}
): string {
	if (!design) return `${selector} {\n\n}`;
	const compiled = flattenDesignTokens(design.tokens).filter(token => /^[a-z][a-z0-9-]*$/.test(token.id));
	const baseSelection = getDefaultDesignModeSelection(design.modeGroups);
	const rules = [`${selector} {\n${createTokenDeclarations(compiled, baseSelection, dimensionPlatforms)}\n}`];
	const declarations = new Map<string, string[]>();
	const addDeclaration = (selection: DesignModeSelection, token: CompiledDesignToken) => {
		const modeSelector = Object.entries(selection)
			.map(([groupId, modeId]) => `[data-sc-mode-${groupId}="${modeId}"]`)
			.join('');
		const entries = declarations.get(modeSelector) ?? [];
		entries.push(
			...createTokenDeclarations([token], { ...baseSelection, ...selection }, dimensionPlatforms).split('\n')
		);
		declarations.set(modeSelector, entries);
	};

	for (const token of compiled) {
		const affectedGroups = new Set((token.value.overrides ?? []).flatMap(override => Object.keys(override.when)));
		for (const groupId of affectedGroups) {
			const group = design.modeGroups.find(item => item.id === groupId)!;
			// Emit every mode, including the default value. A nested Light scope inside a Dark card must actively
			// reset the inherited custom property instead of relying on the root declaration.
			for (const mode of group.modes) addDeclaration({ [groupId]: mode.id }, token);
		}
		for (const override of token.value.overrides ?? []) {
			if (Object.keys(override.when).length > 1) addDeclaration(override.when, token);
		}
	}
	for (const [modeSelector, entries] of [...declarations].sort(
		([left], [right]) => left.split('][').length - right.split('][').length
	))
		rules.push(`${modeSelector} {\n${entries.join('\n')}\n}`);

	return rules.join('\n');
}
export function getCompatibleDesignTokens(tokens: DesignTokenSet | undefined, property: string): CompiledDesignToken[] {
	return flattenDesignTokens(tokens).filter(token => isDesignTokenTypeCompatible(property, token.type));
}
export function getChangedUsedFontTokenFamilies(
	value: unknown,
	before: DesignTokenSet | undefined,
	after: DesignTokenSet | undefined
): string[] {
	const used = new Set(reviewDesignTokenUsage(value, after).referencedTokens);
	const previous = new Map(flattenDesignTokens(before).map(token => [token.cssVariable, token.value]));
	return [
		...new Set(
			flattenDesignTokens(after)
				.filter(
					token =>
						token.type === 'fontFamily' &&
						used.has(token.id) &&
						!isEqual(previous.get(token.cssVariable), token.value)
				)
				.flatMap(token => [token.value.base, ...(token.value.overrides ?? []).map(item => item.value)])
				.map(String)
		),
	];
}

const ASSET_REFERENCE_REGEXP = /^asset\(\s*([a-z][a-z0-9-]*)\s*\)$/i;

export function resolveDesignAssetReference(
	reference: string,
	design: DesignDefinition | undefined,
	selection: DesignModeSelection
): string | undefined {
	const id = reference.trim().match(ASSET_REFERENCE_REGEXP)?.[1];
	if (!id) return undefined;
	const asset = design?.assets.assets.find(item => item.id === id);
	return asset ? resolveDesignModeValue(asset.value, selection) : undefined;
}

export function compileDesignAssetsForAI(design?: DesignDefinition) {
	return (design?.assets.assets ?? []).map(asset => ({
		id: asset.id,
		name: asset.name,
		role: asset.role,
		type: asset.type,
		value: asset.value,
		reference: assetIdToReference(asset.id),
		...(asset.description ? { description: asset.description } : {}),
	}));
}

export function reviewDesignAssetUsage(value: unknown, design?: DesignDefinition) {
	const knownIds = new Set(design?.assets.assets.map(asset => asset.id) ?? []);
	const referencedAssets = new Set<string>();
	const unknownReferences = new Set<string>();
	const visit = (current: unknown): void => {
		if (typeof current === 'string') {
			const id = current.trim().match(ASSET_REFERENCE_REGEXP)?.[1];
			if (!id) return;
			(knownIds.has(id) ? referencedAssets : unknownReferences).add(id);
			return;
		}
		if (Array.isArray(current)) current.forEach(visit);
		else if (current && typeof current === 'object') Object.values(current).forEach(visit);
	};
	visit(value);
	return {
		referencedAssets: [...referencedAssets].sort(),
		unknownReferences: [...unknownReferences].sort(),
	};
}

export function resolveDesignTokenReference(
	reference: string,
	tokens?: DesignTokenSet
): CompiledDesignToken | undefined {
	const cssVariable = reference.trim().match(EXACT_TOKEN_VARIABLE_REGEXP)?.[1];
	if (!cssVariable) return undefined;
	return flattenDesignTokens(tokens).find(token => token.cssVariable === cssVariable);
}

export function reviewDesignTokenUsage(value: unknown, designTokens?: DesignTokenSet) {
	const tokens = flattenDesignTokens(designTokens);
	const tokenPathByVariable = new Map(tokens.map(token => [token.cssVariable, token.id]));
	const incompatibleReferences = new Set<string>();
	const referencedTokens = new Set<string>();
	const unknownReferences = new Set<string>();
	let referenceCount = 0;

	const visit = (current: unknown, property?: string): void => {
		if (typeof current === 'string') {
			TOKEN_VARIABLE_REGEXP.lastIndex = 0;
			for (const match of current.matchAll(TOKEN_VARIABLE_REGEXP)) {
				const cssVariable = match[1]!;
				referenceCount += 1;
				const path = tokenPathByVariable.get(cssVariable);
				if (path) {
					referencedTokens.add(path);
					const token = tokens.find(t => t.id === path)!;
					if (property && !isDesignTokenTypeCompatible(property, token.type))
						incompatibleReferences.add(`${property}: ${cssVariable}`);
				} else unknownReferences.add(cssVariable);
			}
			return;
		}

		if (Array.isArray(current)) {
			current.forEach(item => visit(item, property));
			return;
		}

		if (current && typeof current === 'object')
			Object.entries(current).forEach(([key, item]) =>
				visit(item, DESIGN_TOKEN_PROPERTY_CONFIG[key] ? key : property)
			);
	};

	visit(value);

	return {
		referenceCount,
		incompatibleReferences: [...incompatibleReferences].sort(),
		referencedTokens: [...referencedTokens].sort(),
		unknownReferences: [...unknownReferences].sort(),
	};
}
